"use client";

import { useState } from "react";
import { useAccount } from "@/contexts/account-context";
import type { AdPlatform } from "@/contexts/account-context";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

function PlatformBadge({ platform }: { platform: AdPlatform }) {
  return (
    <Badge
      variant="outline"
      className={
        platform === "meta"
          ? "text-[10px] px-1.5 py-0 border-blue-500/40 text-blue-600 dark:text-blue-400"
          : "text-[10px] px-1.5 py-0 border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
      }
    >
      {platform === "meta" ? "Meta" : "Google"}
    </Badge>
  );
}

function getSyncEndpoint(platform: AdPlatform): string {
  return platform === "meta" ? "/api/meta/sync" : "/api/google/sync";
}

function getSyncLabel(platform: AdPlatform): string {
  return platform === "meta" ? "Meta" : "Google Ads";
}

export function AccountSwitcher() {
  const { accounts, activeAccount, setActiveAccount, isLoading } = useAccount();
  const [syncing, setSyncing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function handleSync() {
    if (!activeAccount) return;
    setSyncing(true);
    try {
      const endpoint = getSyncEndpoint(activeAccount.platform);
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: activeAccount.id }),
      });

      if (!res.ok) {
        const data = await res.json();
        toast.error(data.error || "Sync failed");
        return;
      }

      const result = await res.json();
      const parts: string[] = [];
      if (result.campaigns_synced) parts.push(`${result.campaigns_synced} campaigns`);
      if (result.adsets_synced) parts.push(`${result.adsets_synced} ad sets`);
      if (result.ad_groups_synced) parts.push(`${result.ad_groups_synced} ad groups`);
      if (result.ads_synced) parts.push(`${result.ads_synced} ads`);
      if (result.keywords_synced) parts.push(`${result.keywords_synced} keywords`);
      if (result.insights_synced) parts.push(`${result.insights_synced} insights`);

      if (parts.length > 0) {
        toast.success(`Synced: ${parts.join(", ")}`);
      } else {
        toast.info("No data to sync");
      }

      if (result.errors?.length > 0) {
        toast.warning(`${result.errors.length} error(s) during sync`);
      }
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  if (isLoading || !activeAccount) return null;

  const platformLabel = getSyncLabel(activeAccount.platform);

  const syncButton = (iconSize: string, btnSize: string) => (
    <Button
      variant="ghost"
      size="icon"
      className={`${btnSize} shrink-0`}
      onClick={() => setConfirmOpen(true)}
      disabled={syncing}
      title={`Sync with ${platformLabel}`}
    >
      {syncing ? (
        <Loader2 className={`${iconSize} animate-spin`} />
      ) : (
        <RefreshCw className={iconSize} />
      )}
    </Button>
  );

  const confirmDialog = (
    <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Sincronizar com {platformLabel}</DialogTitle>
          <DialogDescription>
            Deseja sincronizar campanhas
            {activeAccount.platform === "meta"
              ? ", conjuntos de anuncios, anuncios e criativos"
              : ", grupos de anuncios, keywords e assets"}{" "}
            da conta <strong>{activeAccount.name}</strong>?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => setConfirmOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              handleSync();
            }}
          >
            <RefreshCw className="size-4 mr-2" />
            Sincronizar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (accounts.length <= 1) {
    return (
      <div className="flex items-center gap-1.5">
        <div className="flex-1 flex items-center gap-1.5 px-1 min-w-0">
          <PlatformBadge platform={activeAccount.platform} />
          <span className="text-xs text-muted-foreground truncate">
            {activeAccount.name}
          </span>
        </div>
        {syncButton("size-3", "size-6")}
        {confirmDialog}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <Select
        value={activeAccount.id}
        onValueChange={(id) => {
          const account = accounts.find((a) => a.id === id);
          if (account) setActiveAccount(account);
        }}
      >
        <SelectTrigger className="h-8 text-xs flex-1">
          <SelectValue>
            <span className="flex items-center gap-1.5">
              <PlatformBadge platform={activeAccount.platform} />
              <span className="truncate">{activeAccount.name}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {accounts.map((account) => (
            <SelectItem key={account.id} value={account.id}>
              <span className="flex items-center gap-1.5">
                <PlatformBadge platform={account.platform} />
                <span className="truncate">{account.name}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {syncButton("size-3.5", "size-8")}
      {confirmDialog}
    </div>
  );
}
